import React from 'react'
import {
  ResponsiveContainer,
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from 'recharts'
import { SYNTHETIC_WELLNESS_SLEEP_TEAM } from './syntheticDashboardData'
import { Moon, HeartPulse } from 'lucide-react'

export const WellnessSmallCharts: React.FC = () => {
  const data = SYNTHETIC_WELLNESS_SLEEP_TEAM

  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
      gap: '1rem',
    }}>
      {/* Small Chart 1: Dinámica de Sueño (Horas vs Calidad con escalas independientes) */}
      <div style={{
        background: 'var(--surface-card, #ffffff)',
        border: '1px solid var(--surface-border, #e2e8f0)',
        borderRadius: '10px',
        padding: '1.25rem',
        boxShadow: '0 1px 3px rgba(0,0,0,0.03)',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Moon size={16} color="#4338ca" />
            <h4 style={{ margin: 0, fontSize: '0.95rem', fontWeight: 800, color: 'var(--text-main, #0f172a)' }}>
              Sueño: Duración y Calidad Percibida
            </h4>
          </div>
          <span style={{ fontSize: '0.72rem', color: 'var(--text-muted, #64748b)' }}>
            8 semanas · n=18..20
          </span>
        </div>

        <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary, #475569)', marginBottom: '0.75rem' }}>
          Eje izquierdo: <strong>Horas de sueño</strong> (0–10h, barras). Eje derecho: <strong>Calidad</strong> (1–5, línea índigo).
        </div>

        <div style={{ width: '100%', height: '180px' }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
              <XAxis dataKey="week" tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} />
              <YAxis yAxisId="left" domain={[0, 10]} tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} />
              <YAxis yAxisId="right" orientation="right" domain={[1, 5]} tick={{ fontSize: 11, fill: '#4338ca' }} axisLine={false} tickLine={false} />
              <Tooltip
                contentStyle={{ background: '#0a1320', border: 'none', borderRadius: '6px', color: '#fff', fontSize: '0.75rem' }}
                formatter={(value: any, name: any) => [
                  name === 'avgSleepHours' ? `${value} h/noche` : `${value}/5 puntos`,
                  name === 'avgSleepHours' ? 'Horas de sueño' : 'Calidad percibida',
                ]}
              />
              <Bar yAxisId="left" dataKey="avgSleepHours" fill="#cbd5e1" radius={[4, 4, 0, 0]} name="avgSleepHours" />
              <Line yAxisId="right" type="monotone" dataKey="avgSleepQuality" stroke="#4338ca" strokeWidth={2.5} dot={{ r: 3, fill: '#4338ca' }} name="avgSleepQuality" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Small Chart 2: Bienestar y Fatiga / Recuperación (Métricas separadas) */}
      <div style={{
        background: 'var(--surface-card, #ffffff)',
        border: '1px solid var(--surface-border, #e2e8f0)',
        borderRadius: '10px',
        padding: '1.25rem',
        boxShadow: '0 1px 3px rgba(0,0,0,0.03)',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <HeartPulse size={16} color="var(--brand-red, #b91c1c)" />
            <h4 style={{ margin: 0, fontSize: '0.95rem', fontWeight: 800, color: 'var(--text-main, #0f172a)' }}>
              Fatiga y Recuperación (Escalas 1..5)
            </h4>
          </div>
          <span style={{ fontSize: '0.72rem', color: 'var(--text-muted, #64748b)' }}>
            8 semanas · n=18..20
          </span>
        </div>

        <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary, #475569)', marginBottom: '0.75rem' }}>
          Línea ámbar: <strong>Fatiga subjetiva</strong> (1–5). Línea azul: <strong>Recuperación percibida</strong> (1–5).
        </div>

        <div style={{ width: '100%', height: '180px' }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
              <XAxis dataKey="week" tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} />
              <YAxis domain={[1, 5]} ticks={[1, 2, 3, 4, 5]} tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} />
              <Tooltip
                contentStyle={{ background: '#0a1320', border: 'none', borderRadius: '6px', color: '#fff', fontSize: '0.75rem' }}
                formatter={(value: any, name: any) => [
                  `${value}/5`,
                  name === 'avgFatigue' ? 'Fatiga' : name === 'avgRecovery' ? 'Recuperación' : 'Dolor muscular',
                ]}
              />
              <Line type="monotone" dataKey="avgFatigue" stroke="#d97706" strokeWidth={2} dot={{ r: 3, fill: '#d97706' }} name="avgFatigue" />
              <Line type="monotone" dataKey="avgRecovery" stroke="#0284c7" strokeWidth={2} dot={{ r: 3, fill: '#0284c7' }} name="avgRecovery" />
              <Line type="monotone" dataKey="avgMuscleSoreness" stroke="#b91c1c" strokeWidth={1.5} strokeDasharray="4 4" dot={{ r: 2 }} name="avgMuscleSoreness" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  )
}
